import isElectron from 'is-electron';
import { Embedded } from 'flow/types/App';
import { UaePassLogoutData } from 'flow/types/UaePassLogin';
import { isEmbeddedTypeCti, isTeamsAnyVersion } from 'flow/utils/app-utils';
import { fetchUaePassLogout } from 'flow/utils/uaepass-api-utils';
import { isAllowedUaePassUrl } from 'flow/utils/uae-pass-url';
import { getElectronAPI } from 'flow/utils/electron-api';

const REQUEST_TIMEOUT_MS = 4000;
const POPUP_FEATURES = 'width=480,height=640';

/* A frame inside someone else's page stays cross-site, so only a popup is first-party. */
const isEmbeddedFrame = (embeddedType: Embedded | null | undefined) =>
    isEmbeddedTypeCti(embeddedType) || isTeamsAnyVersion(embeddedType);

let pendingRedirectUrl: string | null = null;

/* Kept in the module because CLEAR_STATE wipes Redux before the redirect is consumed. */
export const takePendingUaePassLogoutRedirect = (): string | null => {
    const url = pendingRedirectUrl;
    pendingRedirectUrl = null;
    return url;
};

const withTimeout = <T>(
    promise: Promise<T>,
    timeoutMs: number,
    fallback: T,
): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeout = new Promise<T>((resolve) => {
        timeoutId = setTimeout(() => resolve(fallback), timeoutMs);
    });

    return Promise.race([promise, timeout]).finally(() =>
        clearTimeout(timeoutId),
    );
};

const fetchLogoutUrls = (): Promise<UaePassLogoutData | null> =>
    fetchUaePassLogout().catch(() => null);

/* The url comes from Configurator, so it is not trusted until it is checked against the
   allowlist. Narrowed to non-null so the callers can use it without re-checking. */
const loadLogoutUrl = async (): Promise<string | null> => {
    const data = await withTimeout(fetchLogoutUrls(), REQUEST_TIMEOUT_MS, null);
    const logoutUrl = data?.uaePassLogoutUrl;

    /* Home returns null unless this session was established through UAE Pass. */
    if (!logoutUrl || !isAllowedUaePassUrl(logoutUrl)) return null;

    return logoutUrl;
};

/* Electron logs out in a hidden main-process window: a top-level hop the renderer cannot make. */
const runElectronLogout = async (url: string): Promise<void> => {
    const electronAPI = getElectronAPI();
    if (!electronAPI) return;

    await electronAPI.uaePassLogout({ uaePassLogoutUrl: url });
};

/* An embedded frame cannot navigate its host away, so the popup is the only first-party
   request available to it. The return page closes the window once it lands. */
const runPopupLogout = async (): Promise<void> => {
    /* Opened before the url fetch: window.open needs the sign-out click still to be recent. */
    const popup = window.open('', '_blank', POPUP_FEATURES);

    const url = await loadLogoutUrl();
    if (!url) {
        popup?.close();
        return;
    }

    /* Nothing left to fall back on, and an iframe would report a logout that never happened. */
    if (!popup) {
        console.warn(
            'UAE Pass logout could not complete: the popup was blocked',
        );
        return;
    }

    popup.location.href = url;
};

/* A top-level page logs out by navigating itself, which is first-party and so always carries
   the cookie. The return page then forwards it to the sign-in screen. */
const runWebLogout = async (): Promise<void> => {
    const url = await loadLogoutUrl();
    if (url) pendingRedirectUrl = url;
};

const runLogout = async (embeddedType: Embedded | null | undefined) => {
    if (isElectron()) {
        const url = await loadLogoutUrl();
        if (url) await runElectronLogout(url);
        return;
    }

    if (isEmbeddedFrame(embeddedType)) {
        await runPopupLogout();
        return;
    }

    await runWebLogout();
};

/* Never rejects and never waits unbounded: signing out must not depend on UAE Pass being reachable. */
export const logoutFromUaePass = async (
    embeddedType?: Embedded | null,
): Promise<void> => {
    try {
        await runLogout(embeddedType);
    } catch (error) {
        console.error('UAE Pass logout failed:', error);
    }
};
